import { useAppTFunction } from '@retail/app/i18n';
import { useToast } from '@retail/hooks';
import {
  AssortmentTagItemsUpdate,
  useAssortmentItemsActions,
  useAssortmentTagActions,
} from '@retail/my-assortment/context';
import { AssortmentItemMinimal, AssortmentItemPriorityDTO } from '@retail/my-assortment/types';
import { ItemPriority } from '@retail/products/types';
import { useCallback, useRef, useState } from 'react';
import { ItemBatch } from './HandleItems';

const toItemDTO = (item: AssortmentItemMinimal) =>
  ({
    mgSupplierId: item.supplier.id,
    mgItemNumber: item.mgItemNumber,
    gtinCode: item.gtinCode,
    validFrom: item.validFrom,
    validTo: item.validTo,
    isStocked: item.isStocked,
    priority: item.priority?.prioritised ? ItemPriority.TEN : ItemPriority.ZERO,
  } as AssortmentItemPriorityDTO);

interface UseAddItemsToAssortmentProps {
  onAllItemsAdded: () => void;
  onItemsRemaining: (rest: AssortmentItemMinimal[]) => void;
}

export const useAddItemsToAssortment = ({
  onAllItemsAdded,
  onItemsRemaining,
}: UseAddItemsToAssortmentProps) => {
  const t = useAppTFunction();
  const toast = useToast();

  const { addItemsToLocationAndCustomerAssortments } = useAssortmentItemsActions();
  const { updateAssortmentTags } = useAssortmentTagActions();

  const [isSaving, setIsSaving] = useState(false);

  const itemsPersisted = useRef(false);

  const resetItemsPersisted = useCallback(() => {
    itemsPersisted.current = false;
  }, []);

  const addItems = useCallback(
    async (
      { items: itemsToAdd, rest }: ItemBatch,
      tagUpdates: AssortmentTagItemsUpdate[] = [],
      customerAssortmentIds: number[] = []
    ) => {
      setIsSaving(true);
      try {
        if (!itemsPersisted.current) {
          try {
            await addItemsToLocationAndCustomerAssortments(
              itemsToAdd.map(toItemDTO),
              customerAssortmentIds,
              { skipToast: true }
            );
          } catch (e) {
            console.error(e);
            toast.error(t('products.errors.generic'));
            return;
          }
          itemsPersisted.current = true;
        }

        if (tagUpdates.length) {
          try {
            await updateAssortmentTags({ updates: tagUpdates, removals: [] }, { skipToast: true });
          } catch (e) {
            console.error(e);
            toast.error(t('myAssortment.actions.itemsAddedTagsFailed'));
            return;
          }
        }

        toast.success(
          t(
            itemsToAdd.length === 1
              ? 'products.mgItem.addItemsToAssortment.itemAdded'
              : 'products.mgItem.addItemsToAssortment.itemsAdded',
            { nrOfItems: itemsToAdd.length }
          )
        );
        itemsPersisted.current = false;

        if (rest.length === 0) {
          onAllItemsAdded();
          return;
        }
        onItemsRemaining(rest);
      } finally {
        setIsSaving(false);
      }
    },
    [
      addItemsToLocationAndCustomerAssortments,
      updateAssortmentTags,
      onAllItemsAdded,
      onItemsRemaining,
      toast,
      t,
    ]
  );

  return { addItems, isSaving, resetItemsPersisted };
};
